import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import TableActionButton from "@components/buttons/TableActionButton";
import CopyButton from "@components/buttons/CopyButton";
import TransactionStatusClass from "@components/classes/TransactionStatusClass";
import { billingServices } from "@services/apis/Billing";
import { GetAffiliateInvoice } from "@services/models/Billing/AffiliateInvoicesModel";
import { ColDef } from "ag-grid-community";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ConstantFunctions from "../../utils/constants_functions";
import { buildCSV, CSVColumn, downloadCSV } from "../../utils/downloadCSV";
import { BillingHandle } from "./Billing";

const ROWS = 100; // limit

const Affiliates = forwardRef<BillingHandle>((_, ref) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const constantFunctions = new ConstantFunctions();

  const [data, setData] = useState<GetAffiliateInvoice[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<{ [key: string]: boolean }>({});
  const [statusLoading, setStatusLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const stopRef = useRef(false); // hard stop when no next page
  const pageRequestedRef = useRef(1);
  const transactionStatusClass = new TransactionStatusClass();
  const gridRef = useRef<any>(null);
  const didInitialFetch = useRef(false);
  const inFlight = useRef(false);
  const fetchRef =
    useRef<(pageNum: number, reset?: boolean) => Promise<void>>(undefined);

  // Optional filters
  const [search, setSearch] = useState<string>("");
  const [referrerUserId, setReferrerUserId] = useState<string>("");
  const [invoiceMonth, setInvoiceMonth] = useState<string>(""); // "YYYY-MM"

  const fetchAffiliateInvoices = async (pageNum: number, reset = false) => {
    if (inFlight.current) return;
    if (!reset && stopRef.current) return;
    if (pageNum <= pageRequestedRef.current && !reset) return;

    inFlight.current = true;
    pageRequestedRef.current = pageNum;

    try {
      setError(null);
      if (reset) setLoading(true);
      else setLoadingMore(true);

      const res = await billingServices.getAdminAffiliateInvoices({
        page: pageNum,
        limit: ROWS,
        search: search || undefined,
        referrerUserId: referrerUserId || undefined,
        invoiceMonth: invoiceMonth || undefined,
      });

      const rows: GetAffiliateInvoice[] = res.data.invoices ?? [];
      const p = res.data.pagination;

      const next = !!p?.hasNextPage;

      // ✅ hard stop immediately (ref updates are synchronous)
      stopRef.current = !next;

      setHasMore(next);
      setData((prev) => (reset ? rows : [...prev, ...rows]));
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      inFlight.current = false;
    }
  };

  fetchRef.current = fetchAffiliateInvoices;

  // initial load
  useEffect(() => {
    if (didInitialFetch.current) return;
    didInitialFetch.current = true;

    stopRef.current = false;
    pageRequestedRef.current = 0;

    setPage(1);
    setHasMore(true);
    fetchAffiliateInvoices(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // filters change -> reset (skip initial mount, only react to actual filter changes)
  const filtersReady = useRef(false);
  useEffect(() => {
    if (!filtersReady.current) {
      filtersReady.current = true;
      return;
    }

    stopRef.current = false;
    pageRequestedRef.current = 0;
    inFlight.current = false;

    setPage(1);
    setHasMore(true);
    fetchAffiliateInvoices(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, referrerUserId, invoiceMonth]);

  const onBodyScroll = useCallback((params: any) => {
    if (stopRef.current || inFlight.current) return;

    const api = params.api;
    const displayedRowCount = api.getDisplayedRowCount();
    const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

    if (
      lastDisplayedRowIndex >= displayedRowCount - 5 &&
      displayedRowCount > 0
    ) {
      const nextPage = pageRequestedRef.current + 1;
      setPage(nextPage);
      fetchRef.current?.(nextPage, false);
    }
  }, []);

  const exportToCSV = async () => {
    const EXPORT_LIMIT = 500;
    let allRows: GetAffiliateInvoice[] = [];
    let currentPage = 1;
    let hasMoreData = true;

    while (hasMoreData) {
      const res = await billingServices.getAdminAffiliateInvoices({
        page: currentPage,
        limit: EXPORT_LIMIT,
        search: search || undefined,
        referrerUserId: referrerUserId || undefined,
        invoiceMonth: invoiceMonth || undefined,
      });
      const rows = res.data.invoices ?? [];
      allRows = [...allRows, ...rows];
      hasMoreData = !!res.data.pagination?.hasNextPage;
      currentPage++;
    }

    const csvColumns: CSVColumn[] = [
      {
        header: "Date",
        field: "issuedAt",
        formatter: (v) => (v ? constantFunctions.dateFormatInDDMMYY(v) : ""),
      },
      { header: "Invoice ID", field: "invoiceId" },
      { header: "Invoice Number", field: "invoiceNumber" },
      { header: "Period", field: "invoiceMonth" },
      { header: "Affiliate User ID", field: "referrerUserId" },
      { header: "Affiliate Name", field: "referrerName" },
      { header: "Affiliate Country", field: "referrerCountry" },
      { header: "User ID", field: "referredUserId" },
      { header: "Merchant ID", field: "referredMerchantId" },
      { header: "Legal Entity Name", field: "referredLegalEntityName" },
      { header: "DBA", field: "referredDBA" },
      { header: "Location", field: "referredCountryOfIncorporation" },
      { header: "Service", field: "service" },
      { header: "Direction", field: "direction" },
      { header: "Volume", field: "volume" },
      { header: "Amount", field: "total" },
      { header: "Currency", field: "currencyCode" },
      { header: "Status", field: "status" },
    ];

    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const csv = buildCSV(allRows, csvColumns);
    downloadCSV(csv, `affiliate-invoices-${formattedDate}.csv`);
  };

  useImperativeHandle(ref, () => ({ exportToCSV }));

  const onGridReady = useCallback((params: any) => {
    gridRef.current = params;
  }, []);

  const moneyFormatter = (value: any, currency?: string) => {
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return value ?? "-";
    return currency
      ? `${num.toLocaleString()} ${currency}`
      : num.toLocaleString();
  };

  const handlePdfAction = useCallback(
    async (invoiceId: number, userId: number, isView: boolean) => {
      const key = `${invoiceId}-${isView ? "view" : "download"}`;

      if (pdfLoading[key]) return;

      setPdfLoading((prev) => ({ ...prev, [key]: true }));

      try {
        const { blob: pdfBlob, fileName } =
          await billingServices.affiliateBillingPdf({
            invoiceId,
          });

        const blob =
          pdfBlob instanceof Blob
            ? pdfBlob
            : new Blob([pdfBlob], { type: "application/pdf" });

        if (isView) {
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank");
          setTimeout(() => URL.revokeObjectURL(url), 10000);
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName || `invoice-${invoiceId}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error handling PDF:", error);
      } finally {
        setPdfLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [pdfLoading],
  );

  const handleViewPdf = useCallback(
    (invoiceId: number, userId: number) => {
      handlePdfAction(invoiceId, userId, true);
    },
    [handlePdfAction],
  );

  const handleDownloadPdf = useCallback(
    (invoiceId: number, userId: number) => {
      handlePdfAction(invoiceId, userId, false);
    },
    [handlePdfAction],
  );

  const handleStatusUpdate = useCallback(
    async (invoiceId: number, newStatus: string) => {
      const key = `${invoiceId}-${newStatus}`;

      if (statusLoading[key]) return;

      setStatusLoading((prev) => ({ ...prev, [key]: true }));

      try {
        await billingServices.updateReferralInvoiceStatus({
          invoiceId,
          status: newStatus as "draft" | "issued" | "paid" | "cancelled",
        });

        setData((prevData) =>
          prevData.map((item) =>
            item.invoiceId === invoiceId
              ? { ...item, status: newStatus }
              : item,
          ),
        );
      } catch (error) {
        console.error("Error updating status:", error);
      } finally {
        setStatusLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [statusLoading],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "issuedAt",
        width: 120,
        valueFormatter: (p) =>
          p.value ? constantFunctions.dateFormatInDDMMYY(p.value) : "-",
      },

      {
        headerName: "Invoice ID",
        field: "invoiceId",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Invoice Number",
        field: "invoiceNumber",
        width: 110,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="Invoice Number copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Period",
        field: "invoiceMonth",
        width: 120,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Affiliate User ID",
        field: "referrerUserId",
        width: 110,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },
      {
        headerName: "Affiliate Name",
        field: "referrerName",
        width: 110,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Affiliate Country",
        field: "referrerCountry",
        width: 130,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "User ID",
        field: "referredUserId",
        width: 110,
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => (
          <div className="flex items-center gap-2 h-full">
            <span>{params.value}</span>
            <CopyButton
              copyText={params.value?.toString() || ""}
              copyMessage="User ID copied!"
              copyIconSize={10}
              buttonSize="14px"
              borderRadius="2px"
            />
          </div>
        ),
      },

      {
        headerName: "Merchant ID",
        field: "referredMerchantId",
        width: 120,
        filter: "agNumberColumnFilter",
      },

      {
        headerName: "Legal Entity Name",
        field: "referredLegalEntityName",
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Account Name",
        field: "referredLegalEntityName",
        minWidth: 160,
        wrapText: true,
        autoHeight: true,
      },

      {
        headerName: "DBA",
        field: "referredDBA",
        minWidth: 160,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Location",
        field: "referredCountryOfIncorporation",
        minWidth: 160,
        valueFormatter: (p) => p.value || "-",
      },

      {
        headerName: "Service",
        field: "service",
        minWidth: 180,
      },

      {
        headerName: "Direction",
        field: "direction",
        width: 110,
        cellRenderer: ({ data }: any) => {
          const categoryStyle = transactionStatusClass.getTransactionStatusIcon(
            data.direction.toLowerCase(),
          );
          return (
            <div
              className={`inline-flex h-[20px] items-center justify-center gap-1 whitespace-nowrap rounded px-[6px] text-[10px] font-medium ${categoryStyle.bgColor}`}
              style={{ color: categoryStyle.textColor }}>
              {data.direction.toUpperCase() || "-"}
            </div>
          );
        },
      },

      {
        headerName: "Volume",
        field: "volume",
        width: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => moneyFormatter(p.value),
      },

      {
        headerName: "Amount",
        field: "total",
        width: 140,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) => moneyFormatter(p.value),
      },

      {
        headerName: "Currency",
        field: "currencyCode",
        width: 95,
      },

      {
        headerName: "PDF",
        field: "pdf",
        width: 190,
        sortable: false,
        filter: false,
        cellRenderer: (params: any) => {
          const invoiceId = params.data.invoiceId;
          const userId = params.data.referrerUserId;
          const viewKey = `${invoiceId}-view`;
          const downloadKey = `${invoiceId}-download`;

          return (
            <div className="flex items-center gap-1.5 h-full">
              <TableActionButton
                variant="neutral"
                icon="visibility"
                loading={Boolean(pdfLoading[viewKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleViewPdf(invoiceId, userId);
                }}
              >
                View
              </TableActionButton>
              <TableActionButton
                variant="primary"
                icon="download"
                loading={Boolean(pdfLoading[downloadKey])}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDownloadPdf(invoiceId, userId);
                }}
              >
                Download
              </TableActionButton>
            </div>
          );
        },
      },

      {
        headerName: "Status",
        field: "status",
        width: 130,
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agTextColumnFilter",
        cellRenderer: (params: any) => {
          const currentStatus = (params.value || "").toLowerCase();
          const invoiceId = params.data.invoiceId;
          const statusOptions = ["draft", "issued", "paid", "cancelled"];

          const getStatusClass = (status: string) => {
            const upperStatus = status.toUpperCase();
            return upperStatus === "PAID"
              ? "bg-bg-status-green text-text-status-green"
              : upperStatus === "DRAFT" || upperStatus === "ISSUED"
                ? "bg-bg-status-yellow text-text-status-yellow"
                : upperStatus === "CANCELLED"
                  ? "bg-bg-status-red text-text-status-red"
                  : "bg-bg-status-yellow text-text-status-yellow";
          };

          const isLoading = Object.keys(statusLoading).some(
            (key) => key.startsWith(`${invoiceId}-`) && statusLoading[key],
          );

          return (
            <div className="flex items-center gap-2 h-full">
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border border-gray-400 border-t-transparent" />
              ) : (
                <select
                  value={currentStatus}
                  onChange={(e) => {
                    if (e.target.value !== currentStatus) {
                      handleStatusUpdate(invoiceId, e.target.value);
                    }
                  }}
                  className={`rounded text-[8px] px-2 py-1 font-medium border-none cursor-pointer ${getStatusClass(currentStatus)}`}
                  disabled={isLoading}>
                  {statusOptions.map((option) => (
                    <option
                      key={option}
                      value={option}
                      className="bg-white text-black">
                      {option.toUpperCase()}
                    </option>
                  ))}
                </select>
              )}
            </div>
          );
        },
      },
    ],
    [
      pdfLoading,
      statusLoading,
      handleViewPdf,
      handleDownloadPdf,
      handleStatusUpdate,
    ],
  );

  return (
    <div
      className="flex-1 py-4 overflow-hidden"
      style={{ height: "calc(100dvh - 70px)" }}>
      {loading && page === 1 ? (
        <AgGridSkeleton
          columnDefs={columnDefs}
          style={{ height: "calc(100vh - 150px)" }}
        />
      ) : (
        <div className="flex flex-col h-full">
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            onBodyScroll={onBodyScroll}
            error={error ?? undefined}
            style={{ height: "calc(100vh - 150px)" }}
            getRowId={(params) => params.data.invoiceId?.toString()}
          />

          {loadingMore && data.length > 0 && (
            <div className="flex items-center justify-center py-4 border-t border-border-subtle">
              <div className="flex items-center space-x-2 text-text-subtle">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-border-subtle border-t-border-button-primary" />
                <span className="text-sm">
                  Loading more affiliate invoices...
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

Affiliates.displayName = "Affiliates";

export default Affiliates;
